import { sessionService } from '@/services/sessionService'
import { adaptContentReviewToCoachSummary } from '@/utils/contentReviewAdapter'

export default {
    namespaced: true,
    state: {
        session: null,
        sessions: [],
        coach: null, // Store overall coach summary (red/amber/green feedback + quality score)
        loading: false,
        error: null,
        pollingInterval: null,
        currentSessionId: null,
        previousSessionId: null, // Track previous session ID to detect changes
        pollingEnabled: true,
        coachPollingActive: false, // Track if centralized coach polling is active
        coachPollingTimeout: null, // Track the timeout ID for coach polling
        contentReview: null, // Store content review data (ContentReviewDTO)
        contentReviewPollingActive: false, // Track if content review polling is active
        contentReviewPollingTimeout: null, // Track the timeout ID for content review polling
        reviews: null, // Store generic reviews data (GenericReviewResponseDTO) - REPLACES criteria
        reviewsPollingActive: false, // Track if reviews polling is active
        reviewsPollingTimeout: null, // Track the timeout ID for reviews polling
        reviewsSummary: null, // Store adapted summary cards for UI display
        translationsPollingActive: false, // Track if centralized translations polling is active
        translationsPollingTimeout: null, // Track the timeout ID for translations polling
        deletingTranslations: [], // Track translations being deleted to prevent auto-recreation: [{ sourceHeadingId, language }]
        personificationsPollingActive: false, // Track if centralized personifications polling is active
        personificationsPollingTimeout: null // Track the timeout ID for personifications polling
    },
    getters: {
        getSession: state => state.session,
        getAllSessions: state => state.sessions,
        getCoach: state => state.coach,
        getSessionProgress: state => {
            return state.session?.agentState?.progress || { completedSections: 0, totalSections: 0, percentage: 0 };
        },
        isLoading: state => state.loading,
        getError: state => state.error,
        isPolling: state => !!state.pollingInterval,
        getCurrentSessionId: state => state.currentSessionId,
        isPollingEnabled: state => state.pollingEnabled,
        isSessionReady: state => {
            if (!state.session) {
                return true;
            }
            const currentState = state.session?.agentState?.currentState;
            return currentState === 'Ready' || currentState === 'New Session' || currentState === 'Error';
        },
        isBriefReady: state => {
            if (!state.session) { 
                return false;
            }
            const currentState = state.session?.agentState?.currentState;
            return currentState === 'Brief ready';
        },
        isCoachReady: state => {
            if (!state.session) {
                return false;
            }
            const currentState = state.session?.agentState?.currentState;
            return currentState === 'Coach ready';
        },
        getCurrentStatus: state => {
            return state.session?.agentState?.currentState || 'Unknown';
        },
        hasUpdatedSections: state => {
            return state.session?.briefSections?.some(section => section.isUpdated) || false;
        },
        getReviews: state => state.reviews,
        getReviewsSummary: state => state.reviewsSummary || [],
        getReviewById: (state) => (sectionId) => {
            const section = state.session?.briefSections?.[0];
            return section?.review_details?.[sectionId];
        },
        isReviewsComplete: state => state.reviews?.reviewsAllComplete || false,
        isReviewsPolling: state => state.reviewsPollingActive,
        getReviewsTotalExpected: state => state.reviews?.totalExpected || 0
    },
    mutations: {
        SET_SESSION(state, session) {
            // Preserve existing translations from nested structure when updating session
            // Translations are client-side only and won't be in server response
            if (state.session && state.session.briefSections && session.briefSections) {
                console.log('[SET_SESSION] Preserving nested translations')

                // For each new section, check if we have existing translations to preserve
                session.briefSections.forEach(newSection => {
                    const existingSection = state.session.briefSections.find(
                        s => s.headingID === newSection.headingID
                    )

                    // If existing section has translations, preserve them
                    if (existingSection && existingSection.translations && existingSection.translations.length > 0) {
                        newSection.translations = existingSection.translations
                    } else if (!newSection.translations) {
                        // Initialize empty translations array if not present
                        newSection.translations = []
                    }
                })
            }

            // Preserve reviewsLoading flag and partial review_details during content refresh
            // These are client-side flags that indicate reviews are being regenerated
            if (state.session && state.session.briefSections && session.briefSections) {
                session.briefSections.forEach(newSection => {
                    const existingSection = state.session.briefSections.find(
                        s => s.headingID === newSection.headingID
                    )

                    // Preserve reviewsLoading flag if it was set
                    if (existingSection && existingSection.reviewsLoading) {
                        console.log('[SET_SESSION] Preserving reviewsLoading flag')
                        newSection.reviewsLoading = existingSection.reviewsLoading
                    }

                    // Preserve partial review_details if server response doesn't have them yet
                    // This prevents flash when reviews are being regenerated
                    if (existingSection && existingSection.review_details && !newSection.review_details) {
                        console.log('[SET_SESSION] Preserving partial review_details during regeneration')
                        newSection.review_details = existingSection.review_details
                    }
                })
            }

            // Preserve agentState if it indicates active processing
            // Prevents flash of empty state when navigating to newly created session with content
            if (state.session && state.session.agentState) {
                const existingState = state.session.agentState.currentState
                const newState = session.agentState?.currentState

                // If existing state is a processing state, preserve it over server's 'New Session' state
                const processingStates = ['Sending...', 'Processing content...', 'Content Ready', 'Reviewing...']
                if (processingStates.includes(existingState) && newState === 'New Session') {
                    console.log('[SET_SESSION] Preserving processing state:', existingState)
                    session.agentState = state.session.agentState
                }
            }

            // Ensure all sections have translations array initialized
            if (session.briefSections) {
                session.briefSections.forEach(section => {
                    if (!section.translations) {
                        section.translations = []
                    }
                })
            }

            state.session = session
        },
		SET_SESSION_STATE(state, sessionState) {
            if(state.session)  {
				state.session.agentState = sessionState
			}
        },
        SET_SESSIONS(state, sessions) {
            state.sessions = sessions
        },
        ADD_SESSION(state, session) {
            state.sessions = [session, ...state.sessions]
            // Sort by updatedAt desc if timestamps present
            state.sessions = [...state.sessions].sort((a, b) => {
                const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0
                const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0
                return bTime - aTime
            })
        },
        RENAME_SESSION(state, { sessionId, name, updatedAt }) {
            state.sessions = state.sessions.map(s => {
                if (s.sessionId !== sessionId) return s
                return {
                    ...s,
                    name,
                    ...(updatedAt ? { updatedAt } : {})
                }
            })
            // Re-sort sessions by updatedAt desc if timestamps available
            state.sessions = [...state.sessions].sort((a, b) => {
                const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0
                const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0
                return bTime - aTime
            })
            if (state.session && state.session.sessionId === sessionId) {
                state.session = {
                    ...state.session,
                    name,
                    ...(updatedAt ? { updatedAt } : {})
                }
            }
        },
        REMOVE_SESSION(state, sessionId) {
            state.sessions = state.sessions.filter(s => s.sessionId !== sessionId)
            if (state.session && state.session.sessionId === sessionId) {
                state.session = null
            }
        },
        TOUCH_SESSION(state, { sessionId, updatedAt }) {
            // Update timestamp for the session in the list
            state.sessions = state.sessions.map(s => {
                if (s.sessionId !== sessionId) return s
                return {
                    ...s,
                    updatedAt: updatedAt || new Date().toISOString()
                }
            })
            // Re-sort by updatedAt desc
            state.sessions = [...state.sessions].sort((a, b) => {
                const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0
                const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0
                return bTime - aTime
            })
            if (state.session && state.session.sessionId === sessionId) {
                state.session = {
                    ...state.session,
                    updatedAt: updatedAt || new Date().toISOString()
                }
            }
        },
        UPDATE_SESSION(state, updates) {
            if (state.session) {
                state.session = { ...state.session, ...updates }
            }
        },
        SET_LOADING(state, loading) {
            state.loading = loading
        },
        SET_ERROR(state, error) {
            state.error = error
        },
        CLEAR_ERROR(state) {
            state.error = null
		},
        SET_COACH(state, coach) {
            state.coach = coach
        },
		CLEAR_COACH(state) {
            state.coach = null
        },
        CLEAR_SESSION(state) {
            state.session = null
            state.coach = null
            state.error = null
        },
        CLEAR_SESSIONS(state) {
            state.sessions = []
        },
        SET_POLLING_INTERVAL(state, intervalId) {
            state.pollingInterval = intervalId
        },
        SET_CURRENT_SESSION(state, sessionId) {
            state.previousSessionId = state.currentSessionId
            state.currentSessionId = sessionId
        },
        SET_PREVIOUS_SESSION(state, sessionId) {
            state.previousSessionId = sessionId
        },
        SET_POLLING_ENABLED(state, enabled) {
            state.pollingEnabled = enabled
        },
        SET_COACH_POLLING_ACTIVE(state, active) {
            state.coachPollingActive = active
        },
        SET_COACH_POLLING_TIMEOUT(state, timeoutId) {
            state.coachPollingTimeout = timeoutId
        },
        UPDATE_BRIEF_SECTION(state, { headingID, sectionData }) {
            if (state.session && state.session.briefSections) {
                const sectionIndex = state.session.briefSections.findIndex(
                    section => section.headingID === headingID
                )
                if (sectionIndex !== -1) {
                    // Update the section with new data and mark as updated
                    state.session.briefSections[sectionIndex] = {
                        ...state.session.briefSections[sectionIndex],
                        ...sectionData,
                        isUpdated: true // Flag to indicate section has been modified
                    }
                }
            }
        },
        UPDATE_REVIEW_DETAILS(state, { sectionIndex, review_details, reviewsLoading }) {
            // Granular mutation that reads state at mutation time (atomic)
            // This prevents race conditions with persona/translation polling
            if (state.session?.briefSections?.[sectionIndex]) {
                const currentSection = state.session.briefSections[sectionIndex]

                // Merge new review_details with existing (incremental)
                const mergedReviewDetails = {
                    ...(currentSection.review_details || {}),
                    ...review_details
                }

                // Update only specific properties, preserving the array and other sections
                state.session.briefSections[sectionIndex] = {
                    ...currentSection,
                    review_details: mergedReviewDetails,
                    reviewsLoading: reviewsLoading
                }
            }
        },
        CLEAR_SECTION_UPDATES(state) {
            // Clear all isUpdated flags from brief sections
            if (state.session && state.session.briefSections) {
                state.session.briefSections = state.session.briefSections.map(section => ({
                    ...section,
                    isUpdated: false
                }))
            }
        },
        SET_ALL_SECTIONS_COACH_LOADING(state, loading) {
            // Set coachLoading flag for all brief sections
            if (state.session && state.session.briefSections) {
                state.session.briefSections = state.session.briefSections.map(section => ({
                    ...section,
                    coachLoading: loading
                }))
            }
        },
        SET_SECTION_COACH_LOADING(state, { headingID, loading }) {
            // Set coachLoading flag for a specific section
            if (state.session && state.session.briefSections) {
                const sectionIndex = state.session.briefSections.findIndex(
                    section => section.headingID === headingID
                );
                if (sectionIndex !== -1) {
                    state.session.briefSections[sectionIndex] = {
                        ...state.session.briefSections[sectionIndex],
                        coachLoading: loading
                    };
                }
            }
        },
        UPDATE_SECTION_COACH_DETAIL(state, { headingID, coachDetail }) {
            // Update coach_detail for a specific section
            if (state.session && state.session.briefSections) {
                const sectionIndex = state.session.briefSections.findIndex(
                    section => section.headingID === headingID
                );
                if (sectionIndex !== -1) {
                    state.session.briefSections[sectionIndex] = {
                        ...state.session.briefSections[sectionIndex],
                        coach_detail: coachDetail,
                        coachLoading: false // Turn off loading for this section
                    };
                }
            }
        },
        SET_CONTENT_REVIEW(state, contentReview) {
            state.contentReview = contentReview
        },
        SET_CONTENT_REVIEW_POLLING_ACTIVE(state, active) {
            state.contentReviewPollingActive = active
        },
        SET_CONTENT_REVIEW_POLLING_TIMEOUT(state, timeoutId) {
            state.contentReviewPollingTimeout = timeoutId
        },
        CLEAR_CONTENT_REVIEW(state) {
            state.contentReview = null
        },
        CLEAR_REVIEWS(state) {
            state.reviews = null
        },
        CLEAR_REVIEWS_SUMMARY(state) {
            state.reviewsSummary = null
        },
        SET_REVIEWS(state, reviews) {
            state.reviews = reviews
        },
        SET_REVIEWS_POLLING_ACTIVE(state, active) {
            state.reviewsPollingActive = active
        },
        SET_REVIEWS_POLLING_TIMEOUT(state, timeoutId) {
            state.reviewsPollingTimeout = timeoutId
        },
        SET_REVIEWS_SUMMARY(state, summary) {
            state.reviewsSummary = summary
        },
        CREATE_TRANSLATION_SECTION(state, { sourceHeadingId, language }) {
            // Add a new translation to the source section's translations array
            if (state.session && state.session.briefSections) {
                const sourceSection = state.session.briefSections.find(
                    section => section.headingID === sourceHeadingId
                )
                if (sourceSection) {
                    // Initialize translations array if it doesn't exist
                    if (!sourceSection.translations) {
                        sourceSection.translations = []
                    }

                    // Check if translation already exists
                    const existingTranslation = sourceSection.translations.find(
                        t => t.language === language
                    )

                    if (!existingTranslation) {
                        // Create translation object
                        const translation = {
                            language: language,
                            text: '',
                            subitems: sourceSection.subitems ? sourceSection.subitems.map(item => ({
                                label: item.label,
                                text: ''
                            })) : [],
                            status: 'unanswered',
                            loading: true
                        }

                        // Add to translations array
                        sourceSection.translations.push(translation)
                        console.log(`[CREATE_TRANSLATION_SECTION] Added ${language} translation to section ${sourceHeadingId}`)
                    }
                }
            }
        },
        UPDATE_TRANSLATION_SECTION(state, { sourceHeadingId, language, translationData }) {
            // Update a translation within the source section's translations array
            if (state.session && state.session.briefSections) {
                const sourceSection = state.session.briefSections.find(
                    section => section.headingID === sourceHeadingId
                )
                if (sourceSection && sourceSection.translations) {
                    const translation = sourceSection.translations.find(
                        t => t.language === language
                    )
                    if (translation) {
                        // Update translation properties
                        Object.assign(translation, translationData, { loading: false })
                        console.log(`[UPDATE_TRANSLATION_SECTION] Updated ${language} translation for section ${sourceHeadingId}`)
                    }
                }
            }
        },
        SET_TRANSLATIONS_POLLING_ACTIVE(state, active) {
            state.translationsPollingActive = active
        },
        SET_TRANSLATIONS_POLLING_TIMEOUT(state, timeoutId) {
            state.translationsPollingTimeout = timeoutId
        },
        ADD_DELETING_TRANSLATION(state, { sourceHeadingId, language }) {
            // Track that this translation is being deleted to prevent auto-recreation
            const exists = state.deletingTranslations.some(
                t => t.sourceHeadingId === sourceHeadingId && t.language === language
            )
            if (!exists) {
                state.deletingTranslations.push({ sourceHeadingId, language })
                console.log(`[ADD_DELETING_TRANSLATION] Tracking ${language} deletion for section ${sourceHeadingId}`)
            }
        },
        REMOVE_DELETING_TRANSLATION(state, { sourceHeadingId, language }) {
            // Remove from deleting list after confirmed deleted
            state.deletingTranslations = state.deletingTranslations.filter(
                t => !(t.sourceHeadingId === sourceHeadingId && t.language === language)
            )
            console.log(`[REMOVE_DELETING_TRANSLATION] Stopped tracking ${language} deletion for section ${sourceHeadingId}`)
        },
        DELETE_TRANSLATION_SECTION(state, { sourceHeadingId, language }) {
            // Remove the translation from the source section's translations array
            if (state.session && state.session.briefSections) {
                const sourceSection = state.session.briefSections.find(
                    section => section.headingID === sourceHeadingId
                )
                if (sourceSection && sourceSection.translations) {
                    const translationIndex = sourceSection.translations.findIndex(
                        t => t.language === language
                    )
                    if (translationIndex !== -1) {
                        sourceSection.translations.splice(translationIndex, 1)
                        console.log(`[DELETE_TRANSLATION_SECTION] Removed ${language} translation from section ${sourceHeadingId}`)
                    }
                }
            }
        },
        DELETE_PERSONA_SECTION(state, { headingId }) {
            // Remove a persona section from briefSections
            if (state.session && state.session.briefSections) {
                const sectionIndex = state.session.briefSections.findIndex(
                    section => section.headingID === headingId
                )
                if (sectionIndex !== -1) {
                    state.session.briefSections.splice(sectionIndex, 1)
                    console.log(`[DELETE_PERSONA_SECTION] Removed persona section ${headingId}`)
                }
            }
        },
        SET_PERSONA_SECTION_LOADING(state, { headingId, loading }) {
            // Set loading state for a specific persona section
            if (state.session && state.session.briefSections) {
                const section = state.session.briefSections.find(
                    s => s.headingID === headingId
                )
                if (section) {
                    section.personaLoading = loading
                }
            }
        },
        SET_PERSONIFICATIONS_POLLING_ACTIVE(state, active) {
            state.personificationsPollingActive = active
        },
        SET_PERSONIFICATIONS_POLLING_TIMEOUT(state, timeoutId) {
            state.personificationsPollingTimeout = timeoutId
        },
        CREATE_PERSONA_SECTION(state, { personaSection }) {
            // Add a new persona section to briefSections
            if (state.session && state.session.briefSections) {
                // Check if section already exists
                const existingSection = state.session.briefSections.find(
                    section => section.headingID === personaSection.headingID
                )

                if (!existingSection) {
                    // Add new persona section with loading state
                    state.session.briefSections.push({
                        ...personaSection,
                        personaLoading: true,
                        translations: [] // Initialize translations array
                    })
                    console.log(`[CREATE_PERSONA_SECTION] Added persona section ${personaSection.headingID}`)
                }
            }
        },
        UPDATE_PERSONA_SECTION(state, { headingId, personaData }) {
            // Update a persona section in briefSections
            if (state.session && state.session.briefSections) {
                const section = state.session.briefSections.find(
                    s => s.headingID === headingId
                )
                if (section) {
                    // Update section properties
                    Object.assign(section, personaData, { personaLoading: false })
                    console.log(`[UPDATE_PERSONA_SECTION] Updated persona section ${headingId}`)
                }
            }
        }
    },
    actions: {
        /**
         * Get session by ID
         */
        async getSession({ commit, state }, sessionId) {
            // Only show loading if we're changing to a different session
            const isChangingSession = state.currentSessionId !== sessionId
            if (isChangingSession) {
                commit('SET_LOADING', true)
                // Clear reviews when switching to a different session
                commit('CLEAR_REVIEWS')
                commit('CLEAR_REVIEWS_SUMMARY')
            }
            commit('CLEAR_ERROR')

            try {
                const session = await sessionService.getSession(sessionId)
                commit('SET_SESSION', session)
                commit('SET_CURRENT_SESSION', sessionId)
                return session
            } catch (error) {
                const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch session'
                commit('SET_ERROR', errorMessage)
                throw error
            } finally {
                if (isChangingSession) {
                    commit('SET_LOADING', false)
                }
            }
        },
		/**
        * Get session state by ID
        */
        async getSessionState({ commit }, sessionId) {

            commit('CLEAR_ERROR')

            try {
                const sessionState = await sessionService.getSessionState(sessionId)
                if (sessionState) {
                    commit('SET_SESSION_STATE', sessionState)
                    return sessionState
				}
            } catch (error) {
                const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch session'
                commit('SET_ERROR', errorMessage)
                throw error
            }
        },

        /**
         * DEPRECATED: Start centralized coach polling
         * Legacy function - delegates to pollContentReview which is also legacy
         * Should be replaced with startReviewsPolling in future refactor
         * Idempotent - safe to call multiple times (won't start duplicate polls)
         * @param {boolean|string} loadingTarget - true = all sections, headingID = specific section, false/null = no loading change
         */
        async startCoachPolling({ commit, dispatch, state }, loadingTarget = false) {
            // If already polling, just update loading state for new section if provided
			console.log("startCoachPolling - active = ", state.coachPollingActive);
            if (state.coachPollingActive) {
                if (typeof loadingTarget === 'string') {
                    // A specific section was edited while poll is running
                    console.log('Coach polling already active, setting loading for section:', loadingTarget);
                    commit('SET_SECTION_COACH_LOADING', { headingID: loadingTarget, loading: true });
                } else {
                    console.log('Coach polling already active');
                }
                return;
            }

            // Need a session ID to poll
            if (!state.currentSessionId) {
                console.warn('No current session ID, cannot start coach polling');
                return;
            }

            console.log('Starting centralized coach polling');
            commit('SET_COACH_POLLING_ACTIVE', true);

            // Set loading based on target
            if (loadingTarget === true) {
                // Page refresh or new brief - all sections loading
                console.log('Setting all sections to loading');
                commit('SET_ALL_SECTIONS_COACH_LOADING', true);
            } else if (typeof loadingTarget === 'string') {
                // Single section edit - only that section loading
                console.log('Setting section to loading:', loadingTarget);
                commit('SET_SECTION_COACH_LOADING', { headingID: loadingTarget, loading: true });
            }
            // else: false/null = don't change loading state

            // Start the polling loop
            //dispatch('pollCoachSections');//this is for briefbot
			dispatch('pollContentReview', state.currentSessionId)
        },

        /**
         * Stop centralized coach polling
         * Note: Does NOT turn off spinners - they turn off individually when each section gets its coach_detail
         */
        stopCoachPolling({ commit, state }) {
            if (state.coachPollingTimeout) {
                clearTimeout(state.coachPollingTimeout);
                commit('SET_COACH_POLLING_TIMEOUT', null);
            }
            commit('SET_COACH_POLLING_ACTIVE', false);
            // Don't turn off spinners here - UPDATE_SECTION_COACH_DETAIL handles that individually
            console.log('Coach polling stopped');
        },

        /**
         * Get all sessions
         */
        async getAllSessions({ commit }) {
            commit('SET_LOADING', true)
            commit('CLEAR_ERROR')

            try {
                const sessions = await sessionService.getAllSessions()
                commit('SET_SESSIONS', sessions)
                return sessions
            } catch (error) {
                const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch sessions'
                commit('SET_ERROR', errorMessage)
                throw error
            } finally {
                commit('SET_LOADING', false)
            }
        },

        /**
         * Update session data directly (without fetching from server)
         */
        updateSession({ commit }, { session }) {
            commit('SET_SESSION', session)
        },

        /**
         * Clear session data
         */
        clearSession({ commit }) {
            commit('CLEAR_SESSION')
        },

        /**
         * Clear coach data
         */
        clearCoach({ commit }) {
            commit('CLEAR_COACH')
        },

        /**
         * Clear all sessions data
         */
        clearSessions({ commit }) {
            commit('CLEAR_SESSIONS')
        },

        /**
         * Refresh sessions (reload from server)
         */
        async refreshSessions({ dispatch }) {
            return await dispatch('getAllSessions')
        },

        /**
         * Delete a session by ID
         */
        async deleteSession({ commit, state, dispatch }, sessionId) {
            commit('CLEAR_ERROR')
            try {
                await sessionService.deleteSession(sessionId)
                commit('REMOVE_SESSION', sessionId)
                // If the deleted session is the current one, clear related state
                if (state.currentSessionId === sessionId) {
                    commit('SET_CURRENT_SESSION', null)
                    commit('CLEAR_REVIEWS')
                    commit('CLEAR_REVIEWS_SUMMARY')
                    // Stop any active reviews polling
                    dispatch('stopReviewsPolling')
                    dispatch('chat/clearChat', null, { root: true })
                }
            } catch (error) {
                const errorMessage = error.response?.data?.message || error.message || 'Failed to delete session'
                commit('SET_ERROR', errorMessage)
                throw error
            }
        },

        /**
         * Rename a session by ID
         */
        async renameSession({ commit }, { sessionId, name }) {
            const updated = await sessionService.renameSession(sessionId, name)
            // Handle both updated object and empty body cases
            commit('RENAME_SESSION', {
                sessionId,
                name: updated?.name || name,
                updatedAt: updated?.updatedAt
            })
            return updated
        },

        /**
         * Optimistically bump session updatedAt to now to reflect activity
         */
        touchSession({ commit }, { sessionId, updatedAt }) {
            commit('TOUCH_SESSION', { sessionId, updatedAt })
        },

        /**
         * Create a new session with a name
         */
        async createSession({ commit }, name) {
            const created = await sessionService.createSession(name)
            // Expecting { sessionId, name, createdAt, updatedAt }
            commit('ADD_SESSION', created)
            return created
        },

       


        clearSessionData({ commit }) {
            commit('CLEAR_SESSION')
            commit('CLEAR_ERROR')
            commit('SET_CURRENT_SESSION', null)
            commit('SET_PREVIOUS_SESSION', null)
        },

        /**
         * Submit content for review (Content Reviewer feature)
         * @param {string} sessionId - The session ID
         * @param {string} content - The content to review
         * @param {boolean} rewriteMode - Whether to rewrite/improve content
         */
        async submitContent({ commit, dispatch }, { sessionId, content, rewriteMode = false }) {
            commit('SET_LOADING', true)
            commit('CLEAR_ERROR')
            commit('CLEAR_CONTENT_REVIEW')
            commit('CLEAR_REVIEWS')
            commit('CLEAR_REVIEWS_SUMMARY')

            try {
                // Set agent state to "Sending..." immediately
				commit('SET_SESSION_STATE', {
                        currentState: 'Sending...'
                    })

                // Fire-and-forget: Send to N8N (response is immediate and empty)
                // Don't await - just catch errors for logging
                sessionService.submitContent(sessionId, content, rewriteMode)
                    .catch(error => {
                        console.error('[Submit] Failed to submit content to N8N:', error)
                        commit('SET_ERROR', 'Failed to submit content')
                    })

                console.log('[Submit] Content sent to N8N, starting Phase 1 polling')

                // Touch session to update timestamp
                dispatch('touchSession', { sessionId, updatedAt: new Date().toISOString() })

                // Start Phase 1: Poll for content processing completion
                setTimeout(() => {
                    dispatch('pollContentProcessing', sessionId)
                }, 2000)

            } catch (error) {
                const errorMessage = error.response?.data?.message || error.message || 'Failed to submit content'
                commit('SET_ERROR', errorMessage)
                throw error
            } finally {
                commit('SET_LOADING', false)
            }
        },

        /**
         * Content processing polling loop (Phase 1)
         * Checks /state endpoint until agentState is "Content ready"
         */
        async pollContentProcessing({ commit, getters, dispatch }, sessionId) {
            try {
                const agentState = await sessionService.getSessionState(sessionId)
                console.log('[Phase 1] Agent state:', agentState)

                // Update agent state
				commit('SET_SESSION_STATE', agentState)
                /*commit('UPDATE_SESSION', {
                    agentState: {
                        currentState: agentState.currentState || 'Processing content...'
                    }
                })*/
                if (getters.isSessionReady) {
                    console.log('Session status is Ready, stopping polling');
                }
                // Check if content is ready
                else if (agentState.currentState === 'Content Ready') {
                    console.log('[Phase 1] Content ready!');
                    dispatch('getSessionContent', sessionId);
                } else {
                    // Continue polling in 2 seconds
                    console.log('[Phase 1] Content not ready yet, checking again in 2 seconds');
                    setTimeout(() => {
                        dispatch('pollContentProcessing', sessionId)
                    }, 2000)
                }
            } catch (error) {
                console.error('[Phase 1] Error polling content processing:', error);
                // If error, try to start review polling anyway
                //dispatch('startContentReviewPolling', sessionId)
            }
        },

        async getSessionContent({ commit, dispatch }, sessionId){
        // Fetch session to get raw/processed content
            console.log('Fetching processed content...');
            try {
                const session = await sessionService.getSession(sessionId)
                console.log('[Phase 1] Session content retrieved:', session)

                // Update session with content
                commit('SET_SESSION', session)

                console.log('[Phase 1] Starting Phase 2: personifications, translations, and reviews polling')
                // Start Phase 2: Poll for review results
                //dispatch('startContentReviewPolling', sessionId) // OLD - Replaced by startReviewsPolling
				// TEMPORARILY DISABLED - N8N service not ready yet
				//dispatch('startCoachPolling', true)
				// Start generic reviews polling (REPLACES criteria review polling)
                dispatch('startReviewsPolling', sessionId)
                // Start personifications polling first and wait for initial response
                await dispatch('startPersonificationsPolling')
                // Start translations polling after personifications completes
                dispatch('startTranslationsPolling')
                
            } catch (sessionError) {
                console.error('[Phase 1] Error fetching session content:', sessionError)
                // Continue to Phase 2 even if content fetch fails
                //dispatch('startContentReviewPolling', sessionId)
				//dispatch('startCoachPolling', true)
            }
        },
        /**
         * Content review polling loop
         * Fetches content review and schedules next poll if not complete
         */
        async pollContentReview({ commit, dispatch, state }, sessionId) {
            //if (!state.contentReviewPollingActive) {
			if(!state.coachPollingActive){
                console.log('Content review polling not active, stopping')
                return
            }

            try {
                const contentReview = await sessionService.getContentReview(sessionId)
                console.log('[Phase 2] Content review poll response:', contentReview)

                // Store the content review data
                commit('SET_CONTENT_REVIEW', contentReview)

                // Calculate progress message
                const completedCount = contentReview.criteriaReviews?.filter(c => c.status === 'completed').length || 0
                const totalCount = 5
                const progressMessage = contentReview.status === 'completed'
                    ? 'Ready'
                    : `Reviewing... (${completedCount}/${totalCount} criteria)`

                // Update coach_details on the existing briefSection (don't replace the whole section!)
                // The content text was already set in Phase 1 - we only need to add coach_details
                if (state.session && state.session.briefSections && state.session.briefSections.length > 0) {
                    const existingSection = state.session.briefSections[0]

                    // MERGE new criteria into existing coach_details (N8N returns incremental updates)
                    // Start with existing coach_details to preserve previously completed criteria
                    const coach_details = { ...(existingSection.coach_details || {}) }

                    // Add/update criteria from this poll
                    contentReview.criteriaReviews?.forEach(criterion => {
                        if (criterion.reviewDetail) {
                            coach_details[criterion.criterionId] = {
                                strengths: criterion.reviewDetail.strengths || '',
                                gaps: criterion.reviewDetail.gaps || '',
                                questions: criterion.reviewDetail.questions || '',
                                examples: criterion.reviewDetail.examples || '',
                                summary: criterion.reviewDetail.summary || '',
                                status_label: criterion.reviewDetail.statusLabel || 'Pending',
                                quality: criterion.score,
                                status: criterion.score >= 9 ? 'green' : criterion.score >= 7 ? 'amber' : 'red'
                            }
                        }
                    })

                    // Update ONLY coach_details and coachLoading - preserve the text from Phase 1
                    commit('UPDATE_SESSION', {
                        briefSections: [{
                            ...existingSection,
                            coach_details: coach_details,
                            coachLoading: contentReview.status !== 'completed',
                            status: contentReview.status === 'completed' ? 'answered' : 'unanswered'
                        }],
                        agentState: {
                            currentState: progressMessage
                        }
                    })
                } else {
                    // ERROR: No existing section found - Phase 1 must have failed
                    console.error('[Phase 2] CRITICAL: No briefSection found from Phase 1, cannot continue review polling')
                    commit('SET_ERROR', 'Content not loaded - cannot display review results')
                    dispatch('stopContentReviewPolling')
                    return
                }

                // Update coach summary with criteria cards
                const coachSummary = adaptContentReviewToCoachSummary(contentReview)
                commit('SET_COACH', { coachSummary })

                // Check status and continue polling if not complete
                if (contentReview.status === 'completed' || contentReview.status === 'error') {
                    console.log('[Phase 2] Content review complete, stopping polling')
                    dispatch('stopContentReviewPolling')
                } else {
                    // Schedule next poll in 3 seconds
                    console.log(`[Phase 2] Content review in progress (${completedCount}/${totalCount} criteria), scheduling next poll in 3 seconds`)
                    const timeoutId = setTimeout(() => {
                        dispatch('pollContentReview', sessionId)
                    }, 3000)
                    commit('SET_CONTENT_REVIEW_POLLING_TIMEOUT', timeoutId)
                }
            } catch (error) {
                console.error('Error polling content review:', error)
                dispatch('stopContentReviewPolling')
                // Don't throw - just stop polling on error
            }
        },

        /**
         * Stop content review polling
         */
        stopContentReviewPolling({ commit, state }) {
            if (state.contentReviewPollingTimeout) {
                clearTimeout(state.contentReviewPollingTimeout)
                commit('SET_CONTENT_REVIEW_POLLING_TIMEOUT', null)
            }
            commit('SET_CONTENT_REVIEW_POLLING_ACTIVE', false)
			commit('SET_COACH_POLLING_ACTIVE', false);//using this flag instead
            console.log('Content review polling stopped')
        },

        /**
         * Start generic reviews polling (REPLACES criteria review polling)
         * Polls getReviews endpoint until reviewsAllComplete is true
         */
        async startReviewsPolling({ commit, dispatch, state }, sessionId) {
            if (state.reviewsPollingActive) {
                console.log('[Reviews] Polling already active')
                return
            }

            if (!sessionId) {
                console.warn('[Reviews] No session ID provided, cannot start polling')
                return
            }

            console.log('[Reviews] Starting polling for session:', sessionId)
            commit('SET_REVIEWS_POLLING_ACTIVE', true)
            dispatch('pollReviews', sessionId)
        },

        /**
         * Reviews polling loop
         * Fetches reviews and schedules next poll if not complete
         */
        async pollReviews({ commit, dispatch, state }, sessionId) {
            if (!state.reviewsPollingActive) {
                console.log('[Reviews] Polling not active, stopping')
                return
            }

            try {
                const response = await sessionService.getReviews(sessionId)
                console.log('[Reviews] Poll response:', response)

                commit('SET_REVIEWS', response)

                // Build review_details from response (don't read stale state)
                if (state.session?.briefSections?.[0]) {
                    const review_details = {}
                    response.reviews?.forEach(review => {
                        review_details[review.sectionId] = {
                            sectionName: review.sectionName,
                            status: review.status,
                            statusLabel: review.statusLabel,
                            score: review.score,
                            weight: review.weight,
                            summary: review.summary,
                            cards: review.cards,
                            scoreBreakdown: review.scoreBreakdown,
                            metadata: review.metadata
                        }
                    })

                    // Use granular mutation - reads current state atomically at mutation time
                    // This prevents race conditions with persona/translation sections
                    commit('UPDATE_REVIEW_DETAILS', {
                        sectionIndex: 0,
                        review_details: review_details,
                        reviewsLoading: !response.reviewsAllComplete
                    })

                    // Generate summary from merged state (re-read after mutation)
                    const mergedDetails = state.session.briefSections[0].review_details
                    dispatch('generateReviewsSummary', mergedDetails)
                }

                // Check completion
                if (response.reviewsAllComplete) {
                    console.log('[Reviews] All reviews complete, stopping polling')
                    dispatch('stopReviewsPolling')
                } else {
                    // Schedule next poll in 3 seconds
                    const completedCount = response.reviews?.length || 0
                    console.log(`[Reviews] In progress (${completedCount} reviews received), scheduling next poll in 3 seconds`)
                    const timeoutId = setTimeout(() => {
                        dispatch('pollReviews', sessionId)
                    }, 3000)
                    commit('SET_REVIEWS_POLLING_TIMEOUT', timeoutId)
                }
            } catch (error) {
                console.error('[Reviews] Polling error:', error)
                dispatch('stopReviewsPolling')
            }
        },

        /**
         * Stop reviews polling
         */
        stopReviewsPolling({ commit, state }) {
            if (state.reviewsPollingTimeout) {
                clearTimeout(state.reviewsPollingTimeout)
                commit('SET_REVIEWS_POLLING_TIMEOUT', null)
            }
            commit('SET_REVIEWS_POLLING_ACTIVE', false)
            console.log('[Reviews] Polling stopped')
        },

        /**
         * Generate review summary cards for UI display
         */
        generateReviewsSummary({ commit }, reviewDetails) {
            const statusToPriority = (status) => {
                const map = { 'GREEN': 'green', 'AMBER': 'amber', 'RED': 'red' }
                return map[status] || 'amber'
            }

            const cards = Object.entries(reviewDetails).map(([sectionId, review]) => ({
                sectionId: sectionId,
                priority: statusToPriority(review.status),
                badge: review.statusLabel,
                title: review.sectionName,
                text: review.summary,
                score: review.score,
                weight: review.weight // Include weight from N8N for UI display
            }))

            commit('SET_REVIEWS_SUMMARY', cards)
        },

        /**
         * Create a translation section and start centralized polling
         * @param {string} sourceHeadingId - The section heading ID to translate
         * @param {string} language - The target language (french, italian, german, spanish)
         */
        async createTranslationSection({ commit, dispatch, state }, { sourceHeadingId, language }) {
            console.log(`[createTranslationSection] Creating ${language} translation for section ${sourceHeadingId}`)

            // Create the translation in the source section's translations array
            commit('CREATE_TRANSLATION_SECTION', {
                sourceHeadingId,
                language
            })

            // Start centralized translations polling if not already active
            if (!state.translationsPollingActive) {
                dispatch('startTranslationsPolling')
            }
        },

        /**
         * Request a translation for a section (centralized)
         * Handles the complete flow: create placeholder, make N8N request, start polling
         * @param {string} sourceHeadingId - The section heading ID to translate
         * @param {string} language - The target language (french, italian, german, spanish)
         */
        async requestTranslation({ commit, dispatch, state }, { sourceHeadingId, language }) {
            const sessionId = state.currentSessionId
            if (!sessionId) {
                console.error('[requestTranslation] No session ID available')
                throw new Error('No session ID available')
            }

            console.log(`[requestTranslation] Requesting ${language} translation for section ${sourceHeadingId}`)

            try {
                // 1. Create the translation placeholder in the store
                commit('CREATE_TRANSLATION_SECTION', {
                    sourceHeadingId,
                    language
                })

                // 2. Make the N8N API request
                await sessionService.requestTranslation(sessionId, sourceHeadingId, language)
                console.log(`[requestTranslation] Translation request sent successfully for ${language}`)

                // 3. Start centralized translations polling if not already active
                if (!state.translationsPollingActive) {
					dispatch('startTranslationsPolling', true)
                }
            } catch (error) {
                console.error('[requestTranslation] Failed to request translation:', error)

                // Update the translation to error state
                commit('UPDATE_TRANSLATION_SECTION', {
                    sourceHeadingId,
                    language,
                    translationData: {
                        text: 'Failed to request translation. Please try again.',
                        status: 'stopped'
                    }
                })

                throw error
            }
        },

        /**
         * Delete a translation
         * @param {string} sourceHeadingId - The source section heading ID
         * @param {string} language - The language of the translation to delete
         */
        async deleteTranslation({ commit, state }, { sourceHeadingId, language }) {
            const sessionId = state.currentSessionId
            if (!sessionId) {
                console.error('[deleteTranslation] No session ID available')
                throw new Error('No session ID available')
            }

            console.log(`[deleteTranslation] Deleting ${language} translation for section ${sourceHeadingId}`)

            try {
                // Mark as deleting to prevent auto-recreation during race with polling
                commit('ADD_DELETING_TRANSLATION', { sourceHeadingId, language })

                // Remove from store immediately (optimistic delete)
                commit('DELETE_TRANSLATION_SECTION', { sourceHeadingId, language })

                // Call the backend to delete the translation
                await sessionService.deleteTranslation(sessionId, sourceHeadingId, language)

                console.log(`[deleteTranslation] Successfully deleted ${language} translation for section ${sourceHeadingId}`)
                // Note: We keep the translation in deletingTranslations list to prevent auto-recreation
                // It will be cleaned up when polling confirms it's gone from N8N
            } catch (error) {
                console.error('[deleteTranslation] Error:', error)
                // On error, remove from deleting list and restore in store
                commit('REMOVE_DELETING_TRANSLATION', { sourceHeadingId, language })
                // TODO: Could restore the translation in the store here if needed
                throw error
            }
        },

        /**
         * Start centralized translations polling
         */
        async startTranslationsPolling({ commit, dispatch, state }, delay = false) {
            if (state.translationsPollingActive) {
                console.log('Translations polling already active')
                return
            }

            console.log('[Translations Polling] Starting centralized translations polling')
            commit('SET_TRANSLATIONS_POLLING_ACTIVE', true)

            // Start the first poll
			if (delay){
				const timeoutId = setTimeout(() => {
                        dispatch('pollTranslations')
                    }, 5000)
                commit('SET_TRANSLATIONS_POLLING_TIMEOUT', timeoutId)
			} else {
                dispatch('pollTranslations')
			}
        },

        /**
         * Poll all translations for the current session
         */
        async pollTranslations({ commit, dispatch, state }) {
            const sessionId = state.currentSessionId
            if (!sessionId) {
                console.warn('[Translations Polling] No current session ID, cannot poll translations')
                return
            }

            if (!state.translationsPollingActive) {
                console.log('[Translations Polling] Polling stopped')
                return
            }

            try {
                const translationsResponse = await sessionService.getTranslations(sessionId)
                console.log('[Translations Polling] Response:', translationsResponse)

                // Update all translations with their results
                if (translationsResponse.translations && translationsResponse.translations.length > 0) {
                    translationsResponse.translations.forEach(translation => {
                        const language = translation.language
                        const sourceHeadingId = translation.headingId

                        if (!sourceHeadingId) {
                            console.warn('[Translations Polling] No sourceHeadingId for translation:', translation)
                            return
                        }

                        // Find the source section
                        const sourceSection = state.session?.briefSections?.find(
                            section => section.headingID === sourceHeadingId
                        )

                        if (!sourceSection) {
                            console.warn(`[Translations Polling] Source section ${sourceHeadingId} not found`)
                            return
                        }

                        // Check if translation exists in nested structure
                        const translationExists = sourceSection.translations?.some(
                            t => t.language === language
                        )

                        // Check if this translation is being deleted (prevent race condition)
                        const isBeingDeleted = state.deletingTranslations.some(
                            t => t.sourceHeadingId === sourceHeadingId && t.language === language
                        )

                        // If translation doesn't exist locally, create it (handles page refresh case)
                        // BUT don't recreate if it's currently being deleted (race condition protection)
                        if (!translationExists && !isBeingDeleted) {
                            console.log(`[Translations Polling] Auto-creating missing ${language} translation for section ${sourceHeadingId}`)
                            commit('CREATE_TRANSLATION_SECTION', {
                                sourceHeadingId,
                                language
                            })
                        } else if (!translationExists && isBeingDeleted) {
                            console.log(`[Translations Polling] Skipping auto-create for ${language} - marked as deleting`)
                        }

                        // Update the translation with data from N8N
                        if (translation.status === 'completed') {
                            commit('UPDATE_TRANSLATION_SECTION', {
                                sourceHeadingId,
                                language,
                                translationData: {
                                    text: translation.text || '',
                                    subitems: translation.subitems || [],
                                    status: 'answered'
                                }
                            })
                        } else if (translation.status === 'error') {
                            commit('UPDATE_TRANSLATION_SECTION', {
                                sourceHeadingId,
                                language,
                                translationData: {
                                    text: 'Translation failed. Please try again.',
                                    status: 'stopped'
                                }
                            })
                        }
                    })
                }

                // Check if all translations are complete
                if (translationsResponse.translationsAllComplete) {
                    console.log('[Translations Polling] All translations complete, stopping polling')

                    // Clean up deletingTranslations list: Remove any that N8N no longer reports
                    // Only do this when translationsAllComplete=true to ensure we have a stable, complete response
                    // This confirms the deletion was successful on the backend
                    if (state.deletingTranslations.length > 0) {
                        const serverTranslations = translationsResponse.translations || []
                        state.deletingTranslations.forEach(deletingTranslation => {
                            const stillOnServer = serverTranslations.some(
                                t => t.headingId === deletingTranslation.sourceHeadingId &&
                                     t.language === deletingTranslation.language
                            )
                            if (!stillOnServer) {
                                // N8N no longer has this translation - deletion confirmed
                                console.log(`[Translations Polling] Deletion confirmed for ${deletingTranslation.language} on section ${deletingTranslation.sourceHeadingId}`)
                                commit('REMOVE_DELETING_TRANSLATION', {
                                    sourceHeadingId: deletingTranslation.sourceHeadingId,
                                    language: deletingTranslation.language
                                })
                            }
                        })
                    }

                    dispatch('stopTranslationsPolling')
                } else {
                    // Schedule next poll in 3 seconds
                    console.log('[Translations Polling] Translations in progress, scheduling next poll in 3 seconds')
                    const timeoutId = setTimeout(() => {
                        dispatch('pollTranslations')
                    }, 3000)
                    commit('SET_TRANSLATIONS_POLLING_TIMEOUT', timeoutId)
                }
            } catch (error) {
                console.error('[Translations Polling] Error:', error)

                // Set all loading translations to error state
                if (state.session && state.session.briefSections) {
                    state.session.briefSections.forEach(section => {
                        if (section.translations) {
                            section.translations.forEach(translation => {
                                if (translation.loading) {
                                    commit('UPDATE_TRANSLATION_SECTION', {
                                        sourceHeadingId: section.headingID,
                                        language: translation.language,
                                        translationData: {
                                            text: 'Failed to fetch translation status. Please try again.',
                                            status: 'stopped'
                                        }
                                    })
                                }
                            })
                        }
                    })
                }

                dispatch('stopTranslationsPolling')
            }
        },

        /**
         * Stop centralized translations polling
         */
        stopTranslationsPolling({ commit, state }) {
            if (state.translationsPollingTimeout) {
                clearTimeout(state.translationsPollingTimeout)
                commit('SET_TRANSLATIONS_POLLING_TIMEOUT', null)
            }
            commit('SET_TRANSLATIONS_POLLING_ACTIVE', false)
            console.log('[Translations Polling] Stopped')
        },

        /**
         * Delete a persona section
         * @param {string} headingId - The persona section heading ID to delete
         */
        async deletePersonaSection({ commit, state }, { headingId }) {
            const sessionId = state.currentSessionId
            if (!sessionId) {
                console.error('No current session ID, cannot delete persona section')
                throw new Error('No current session ID')
            }

            console.log(`[deletePersonaSection] Deleting persona section ${headingId}`)

            try {
                // Call the backend to delete the persona section
                await sessionService.deletePersonaSection(sessionId, headingId)

                // Remove from store
                commit('DELETE_PERSONA_SECTION', { headingId })

                console.log(`[deletePersonaSection] Successfully deleted persona section ${headingId}`)
            } catch (error) {
                console.error('[deletePersonaSection] Error:', error)
                throw error
            }
        },

        /**
         * Request review for a persona section
         * @param {string} headingId - The persona section heading ID to review
         */
        async requestPersonaReview({ commit, state }, { headingId }) {
            const sessionId = state.currentSessionId
            if (!sessionId) {
                console.error('No current session ID, cannot request persona review')
                throw new Error('No current session ID')
            }

            console.log(`[requestPersonaReview] Requesting review for persona section ${headingId}`)

            try {
                // Set loading state
                commit('SET_PERSONA_SECTION_LOADING', { headingId, loading: true })

                // Call the backend to request review
                await sessionService.requestPersonaReview(sessionId, headingId)

                console.log(`[requestPersonaReview] Review request sent for persona section ${headingId}`)

                // TODO: Start polling for review results when persona review polling is implemented
                // For now, the review will appear when the session is next fetched

            } catch (error) {
                console.error('[requestPersonaReview] Error:', error)
                commit('SET_PERSONA_SECTION_LOADING', { headingId, loading: false })
                throw error
            }
        },

        /**
         * Start centralized personifications polling
         */
        async startPersonificationsPolling({ commit, dispatch, state }) {
            if (state.personificationsPollingActive) {
                console.log('[Personifications Polling] Already active')
                return
            }

            console.log('[Personifications Polling] Starting centralized personifications polling')
            commit('SET_PERSONIFICATIONS_POLLING_ACTIVE', true)

            // Start the first poll and wait for it to complete
            await dispatch('pollPersonifications')
        },

        /**
         * Poll all personifications for the current session
         */
        async pollPersonifications({ commit, dispatch, state }) {
            const sessionId = state.currentSessionId
            if (!sessionId) {
                console.warn('[Personifications Polling] No current session ID, cannot poll personifications')
                return
            }

            if (!state.personificationsPollingActive) {
                console.log('[Personifications Polling] Polling stopped')
                return
            }

            try {
                const personificationsResponse = await sessionService.getPersonifications(sessionId)
                console.log('[Personifications Polling] Response:', personificationsResponse)

                // Update all personifications with their results
                if (personificationsResponse.personifications && personificationsResponse.personifications.length > 0) {
                    personificationsResponse.personifications.forEach(personification => {
                        const headingId = personification.headingId
                        const parentContentId = personification.parentContentId
                        const personaTypes = personification.personaTypes

                        if (!headingId) {
                            console.warn('[Personifications Polling] No headingId for personification:', personification)
                            return
                        }

                        // Check if persona section exists
                        const personaSection = state.session?.briefSections?.find(
                            section => section.headingID === headingId
                        )

                        // If persona section doesn't exist, create it (handles page refresh case)
                        if (!personaSection) {
                            console.log(`[Personifications Polling] Auto-creating missing persona section ${headingId}`)
                            commit('CREATE_PERSONA_SECTION', {
                                personaSection: {
                                    headingID: headingId,
                                    heading: personification.heading || '',
                                    text: '',
                                    subitems: [],
                                    status: 'unanswered',
                                    contentType: 'persona',
                                    parentContentId: parentContentId,
                                    personaTypes: personaTypes
                                }
                            })
                        }

                        // Update the persona section with data from N8N
                        if (personification.status === 'completed') {
                            commit('UPDATE_PERSONA_SECTION', {
                                headingId,
                                personaData: {
                                    heading: personification.heading || '',
                                    text: personification.text || '',
                                    subitems: personification.subitems || [],
                                    status: 'answered',
                                    contentType: 'persona',
                                    parentContentId: parentContentId,
                                    personaTypes: personaTypes
                                }
                            })
                        } else if (personification.status === 'error') {
                            commit('UPDATE_PERSONA_SECTION', {
                                headingId,
                                personaData: {
                                    text: 'Persona-ification failed. Please try again.',
                                    status: 'stopped'
                                }
                            })
                        }
                    })
                }

                // Check if all personifications are complete
                if (personificationsResponse.personificationsAllComplete) {
                    console.log('[Personifications Polling] All personifications complete, stopping polling')

                    // Check for orphaned persona sections still in loading state
                    if (state.session && state.session.briefSections) {
                        state.session.briefSections.forEach(section => {
                            if (section.contentType === 'persona' && section.personaLoading) {
                                console.warn(`[Personifications Polling] Persona section ${section.headingID} still loading after completion - setting error state`)
                                commit('UPDATE_PERSONA_SECTION', {
                                    headingId: section.headingID,
                                    personaData: {
                                        text: 'Persona-ification failed. Please try again.',
                                        status: 'stopped'
                                    }
                                })
                            }
                        })
                    }

                    dispatch('stopPersonificationsPolling')
                } else {
                    // Schedule next poll in 3 seconds
                    console.log('[Personifications Polling] Personifications in progress, scheduling next poll in 3 seconds')
                    const timeoutId = setTimeout(() => {
                        dispatch('pollPersonifications')
                    }, 3000)
                    commit('SET_PERSONIFICATIONS_POLLING_TIMEOUT', timeoutId)
                }
            } catch (error) {
                console.error('[Personifications Polling] Error:', error)

                // Set all loading persona sections to error state
                if (state.session && state.session.briefSections) {
                    state.session.briefSections.forEach(section => {
                        if (section.contentType === 'persona' && section.personaLoading) {
                            commit('UPDATE_PERSONA_SECTION', {
                                headingId: section.headingID,
                                personaData: {
                                    text: 'Failed to fetch persona content. Please try again.',
                                    status: 'stopped'
                                }
                            })
                        }
                    })
                }

                dispatch('stopPersonificationsPolling')
            }
        },

        /**
         * Stop centralized personifications polling
         */
        stopPersonificationsPolling({ commit, state }) {
            if (state.personificationsPollingTimeout) {
                clearTimeout(state.personificationsPollingTimeout)
                commit('SET_PERSONIFICATIONS_POLLING_TIMEOUT', null)
            }
            commit('SET_PERSONIFICATIONS_POLLING_ACTIVE', false)
            console.log('[Personifications Polling] Stopped')
        }

    }
} 